import type { UnattributedBooks } from '../../api/types.js';
import { color, fontSize, radius } from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';

/**
 * The per-figure data-quality indicator (FR-064, FR-059, R-16).
 *
 * Attaches to **BoB and the candidate-status figures derived from it** — not to the page.
 * Scoping it this way is the point: a page-level banner sitting over a perfectly correct TOA
 * column trains people to dismiss it, and then it is not there when it matters. Other columns
 * stay unmarked because the shortfall does not touch them.
 *
 * It **labels** the figure; it does not adjust it. FR-064 asks for the count and a statement
 * that the number is under-reported — inflating BoB by an estimate would be a fabrication, and
 * hiding it would be the "confidently blank" failure FR-065 also guards against.
 *
 * ## It reports the alarm population, and only that (owner decision, 2026-08-28)
 *
 * `unattributedBooks` is a recruiter id that reaches **no master row** — a genuinely broken
 * bridge. It is **structurally zero** since the alignment swap (DATA-SOURCES G-25), so in
 * practice this badge does not appear.
 *
 * ⚠ **The out-of-scope population is deliberately NOT shown here.** Books whose recruiter has no
 * regional alignment — 257 recruiters / 56,683 candidates — are expected by owner decision, and
 * so is the region bound on the KPI strip (FR-029a). Both are recorded in refresh metadata
 * and neither is surfaced on a figure: a note that fires on every snapshot, for a rule the
 * business chose, is the indicator people learn to ignore — and then it is absent when a real
 * fault arrives. An earlier revision rendered both; it was removed on the same day.
 */

export interface DataQualityBadgeProps {
  /** The alarm population only — `dataQuality.unattributedBooks` (FR-059). */
  unattributedBooks: UnattributedBooks | null | undefined;
  /** The figure this badge sits beside, so the announcement says what is affected. */
  figureLabel: string;
}

export function isUnderReported(books: UnattributedBooks | null | undefined): boolean {
  return (books?.candidateCount ?? 0) > 0;
}

export function DataQualityBadge({ unattributedBooks, figureLabel }: DataQualityBadgeProps) {
  if (!isUnderReported(unattributedBooks)) return null;

  const { candidateCount, recruiterCount } = unattributedBooks!;
  const message =
    `${figureLabel} is under-reported: ${candidateCount} candidates across ` +
    `${recruiterCount} recruiter${recruiterCount === 1 ? '' : 's'} could not be resolved ` +
    `to a region in the last refresh.`;

  return (
    <Disclosure accessibleName={message} panel={message}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          borderRadius: radius.circle,
          background: color.warningBg,
          border: `1px solid ${color.warningBorder}`,
          color: color.warning,
          fontSize: '9px',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        !
      </span>
      <span className="sr-only">{message}</span>
    </Disclosure>
  );
}

/** Inline note for a card or summary tile, where a bare glyph would be too quiet. */
export function DataQualityNote({ unattributedBooks, figureLabel }: DataQualityBadgeProps) {
  if (!isUnderReported(unattributedBooks)) return null;

  return (
    <div
      style={{
        marginTop: 4,
        fontSize: fontSize.micro,
        color: color.warning,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <DataQualityBadge unattributedBooks={unattributedBooks} figureLabel={figureLabel} />
      <span>under-reported by {unattributedBooks!.candidateCount}</span>
    </div>
  );
}
